import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef,
} from "@angular/core";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import { HttpClient } from "@app/shared/services/http/http-client.service";
import { ToastrService } from "ngx-toastr";
import { DriverCardData } from "@app/shared/components/driver-card/driver-card.component";
import { getDriverDisplayName } from "@app/shared/utils/driver-name.util";
import { capitalizeWords } from "@app/shared/utils/text.util";
import { FeatureFlagsService } from "@app/agilx/shared/feature-flags.service";
import { ConfigService } from "@app/utilities/config/shared/config.service";

export interface AssignDriverResult {
  team: any;
  boardId: string | null;
  boardName: string | null;
  billToClientId: string | null;
  billToClientName: string | null;
  isUpdate: boolean;
}

@Component({
  selector: "app-assign-driver-modal",
  templateUrl: "./assign-driver-modal.component.html",
  styleUrls: ["./assign-driver-modal.component.css"],
})
export class AssignDriverModalComponent implements OnChanges, OnDestroy {
  /** Whether the modal is visible. Use [(visible)] for two-way binding. */
  @Input() visible = false;
  /** The driver's actual ID (driverId on SchedulingTeamDriver). */
  @Input() driverId: string | null = null;
  /**
   * If the driver is already assigned to a team, supply its ID.
   * When omitted the component auto-detects by searching all team driver lists.
   */
  @Input() existingTeamId: string | null = null;
  /** Pre-selected board ID (for edit mode). */
  @Input() existingBoardId: string | null = null;
  /** Pre-selected board NAME (for edit mode - used when only name is known). */
  @Input() existingBoardName: string | null = null;
  /** Pre-selected bill-to client ID (for edit mode). */
  @Input() existingBillToId: string | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() assignSuccess = new EventEmitter<AssignDriverResult>();
  @Output() updateSuccess = new EventEmitter<AssignDriverResult>();
  @Output() unassignSuccess = new EventEmitter<void>();
  @Output() unassignWithNote = new EventEmitter<{ driverId: string; teamId: string }>();

  // ── Loading ────────────────────────────────────────────────────────────────
  isLoadingDriverDetail = false;
  isLoadingTeams = false;
  isSubmitting = false;

  // ── Data ──────────────────────────────────────────────────────────────────
  driverDetail: DriverCardData | null = null;
  schedulingTeams: any[] = [];
  billToClients: any[] = [];
  tractors: any[] = [];
  trailers: any[] = [];

  // Clients the driver is banned from (ids + names). A driver must never be
  // placed on a scheduling group/board whose client they are banned from.
  bannedClientIds: string[] = [];
  bannedClientNames: string[] = [];

  // ── Selections ─────────────────────────────────────────────────────────────
  selectedTeamId: string | null = null;
  selectedBoardId: string | null = null;
  selectedBillToId: string | null = null;
  selectedTractorId: string | null = null;
  selectedTrailerId: string | null = null;
  notifyOnAssign = false;

  // Tracks whether the user changed tractor/trailer since the modal opened,
  // and the driver's original equipment as loaded from driver detail.
  private equipmentTouched = false;
  private originalTractorId: string | null = null;
  private originalTrailerId: string | null = null;

  // Auto-detected existing team (when existingTeamId is not supplied)
  private detectedExistingTeamId: string | null = null;

  // ── Email compose (survives main modal close) ──────────────────────────────
  showEmailCompose = false;
  emailTo = "";
  emailCc = "";
  emailBcc = "";
  emailSubject = "";
  emailBody = "";
  isSendingEmail = false;
  /** Notes CC address (omahadispatch@lgttransport.com in prod); the notify email is copied to it. */
  private driverNotesCcEmail = "";
  private notesCcLoaded = false;

  // ── Training notes ─────────────────────────────────────────────────────────
  showTrainingNotes = false;
  trainingNotesContent: string | null = null;
  trainingNotesClientName: string | null = null;

  get emailFeaturesEnabled(): boolean {
    return this.featureFlags.isEnabled("emailFeatures");
  }

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private featureFlags: FeatureFlagsService,
    private configService: ConfigService,
    private cdr: ChangeDetectorRef
  ) {}

  private cancelLoad$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  private isDestroyed = false;

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.cancelLoad$.next();
    this.cancelLoad$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Forces the view to re-render after async data arrives. Without this, a
   * response that completes while no change detection pass is scheduled can
   * leave the modal stuck on "Loading..." until the user interacts with it.
   */
  private refreshView(): void {
    if (!this.isDestroyed) {
      this.cdr.detectChanges();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["visible"]) {
      if (this.visible && this.driverId) {
        this.initModal();
      } else if (!this.visible) {
        this.resetMainState();
      }
    }
  }

  private initModal(): void {
    // Cancel any in-flight requests from a previous open
    this.cancelLoad$.next();

    this.driverDetail = null;
    this.schedulingTeams = [];
    this.billToClients = [];
    this.selectedTeamId = null;
    this.selectedBoardId = null;
    this.selectedBillToId = null;
    this.selectedTractorId = null;
    this.selectedTrailerId = null;
    this.detectedExistingTeamId = null;
    this.notifyOnAssign = false;
    this.isSubmitting = false;
    this.equipmentTouched = false;
    this.originalTractorId = null;
    this.originalTrailerId = null;
    this.bannedClientIds = [];
    this.bannedClientNames = [];

    // Keep the notify email CC in step with the note emails.
    this.loadNotesCcEmail();

    // Load driver detail
    this.isLoadingDriverDetail = true;
    this.http
      .get(`api/board/driverDetail/${this.driverId}`)
      .pipe(
        takeUntil(this.cancelLoad$),
        // finalize always runs (complete, error, or unsubscribe), so the loading
        // flag can never be stranded true by a cancelled request.
        finalize(() => {
          this.isLoadingDriverDetail = false;
          this.refreshView();
        })
      )
      .subscribe({
        next: (d: any) => {
          this.driverDetail = d as DriverCardData;
          // Prefill equipment with the driver's current assignments, but do not
          // clobber a selection the user may have already made.
          if (!this.equipmentTouched) {
            this.originalTractorId = (d as any).tractorId ?? null;
            this.originalTrailerId = (d as any).trailerId ?? null;
            this.selectedTractorId = this.originalTractorId;
            this.selectedTrailerId = this.originalTrailerId;
          }
          // Remember the clients the driver is banned from so banned scheduling
          // teams can be flagged and blocked.
          this.bannedClientIds = Array.isArray((d as any).bannedClientIds)
            ? (d as any).bannedClientIds
            : [];
          this.bannedClientNames = Array.isArray((d as any).bannedClients)
            ? (d as any).bannedClients
            : [];
        },
        error: () => {},
      });

    // Load tractors
    this.http
      .get("api/tractor/listDropDown")
      .pipe(
        takeUntil(this.cancelLoad$),
        finalize(() => this.refreshView())
      )
      .subscribe({
        next: (data: any) => {
          this.tractors = Array.isArray(data) ? data.filter((t: any) => t.unitNumber != null) : [];
        },
        error: () => {},
      });

    // Load trailers
    this.http
      .get("api/trailer/listDropDown")
      .pipe(
        takeUntil(this.cancelLoad$),
        finalize(() => this.refreshView())
      )
      .subscribe({
        next: (data: any) => {
          this.trailers = Array.isArray(data) ? data.filter((t: any) => t.trailerNo != null) : [];
        },
        error: () => {},
      });

    // Load scheduling teams
    this.isLoadingTeams = true;
    this.http
      .get("api/board/listAll")
      .pipe(
        takeUntil(this.cancelLoad$),
        // finalize always runs (complete, error, or unsubscribe), so the loading
        // flag can never be stranded true by a cancelled request.
        finalize(() => {
          this.isLoadingTeams = false;
          this.refreshView();
        })
      )
      .subscribe({
        next: (teams: any) => {
          this.schedulingTeams = Array.isArray(teams) ? teams : [];
          try {
            this.prefillSelections();
          } catch (e) {
            console.error("Error processing scheduling teams", e);
          }
        },
        error: () => {
          this.toastr.error("Failed to load scheduling teams");
        },
      });

    // Load bill-to clients
    this.http
      .get("api/board/clients")
      .pipe(
        takeUntil(this.cancelLoad$),
        finalize(() => this.refreshView())
      )
      .subscribe({
        next: (c: any) => {
          this.billToClients = Array.isArray(c) ? c : [];
        },
        error: () => {},
      });
  }

  /**
   * Loads the notes CC address once. The notify-scheduling-team email is copied to it, the same
   * way every driver-note email is (omahadispatch@lgttransport.com in production).
   */
  private loadNotesCcEmail(): void {
    if (this.notesCcLoaded) return;
    this.notesCcLoaded = true;
    this.configService.getDriverNotesCcEmail().subscribe({
      next: (ccEmail: string) => {
        this.driverNotesCcEmail = ccEmail || "";
      },
      error: () => {
        this.notesCcLoaded = false;
      },
    });
  }

  private prefillSelections(): void {
    if (this.existingTeamId) {
      // Parent knows the existing team (boards/dispatch edit mode)
      this.selectedTeamId = this.existingTeamId;
      if (this.existingBoardId) {
        this.selectedBoardId = this.existingBoardId;
      } else if (this.existingBoardName) {
        const team = this.schedulingTeams.find((t: any) => t.id === this.existingTeamId);
        const board = (team?.boards || []).find((b: any) => b.name === this.existingBoardName);
        this.selectedBoardId = board?.id || null;
      }
      this.selectedBillToId = this.existingBillToId;
    } else {
      // Auto-detect: search all teams for this driver (driver-detail mode)
      const existingTeam = this.schedulingTeams.find((t: any) =>
        (t.drivers || []).some((d: any) => d.driverId === this.driverId)
      );
      if (existingTeam) {
        const driverEntry = existingTeam.drivers.find((d: any) => d.driverId === this.driverId);
        this.detectedExistingTeamId = existingTeam.id;
        this.selectedTeamId = existingTeam.id;
        const board = (existingTeam.boards || []).find(
          (b: any) => b.name === driverEntry?.boardName
        );
        this.selectedBoardId = board?.id || driverEntry?.boardId || null;
        this.selectedBillToId = driverEntry?.billToClientId || null;
      }
    }
  }

  private resetMainState(): void {
    // Deliberately does NOT reset email compose or training notes -
    // those dialogs are shown after the main modal has already closed.
    this.driverDetail = null;
    this.schedulingTeams = [];
    this.billToClients = [];
    this.tractors = [];
    this.trailers = [];
    this.selectedTeamId = null;
    this.selectedBoardId = null;
    this.selectedBillToId = null;
    this.selectedTractorId = null;
    this.selectedTrailerId = null;
    this.notifyOnAssign = false;
    this.isSubmitting = false;
    this.isLoadingDriverDetail = false;
    this.isLoadingTeams = false;
    this.detectedExistingTeamId = null;
    this.equipmentTouched = false;
    this.originalTractorId = null;
    this.originalTrailerId = null;
    this.bannedClientIds = [];
    this.bannedClientNames = [];
    // Cancel any in-flight load requests
    this.cancelLoad$.next();
  }

  // ── Computed ───────────────────────────────────────────────────────────────

  get effectiveExistingTeamId(): string | null {
    return this.existingTeamId || this.detectedExistingTeamId;
  }

  get isEditMode(): boolean {
    return !!this.effectiveExistingTeamId;
  }

  get selectedTeam(): any {
    return this.schedulingTeams.find((t: any) => t.id === this.selectedTeamId);
  }

  get availableBoards(): any[] {
    return this.selectedTeam?.boards || [];
  }

  get availableBillToClients(): any[] {
    if (!this.selectedTeam) return [];
    return this.billToClients.filter((c: any) => c.clientId === this.selectedTeam.clientId);
  }

  /**
   * True when the driver is banned from the client a scheduling team belongs to.
   * A driver must never be placed on a scheduling group/board under a banned client.
   */
  isTeamBanned(team: any): boolean {
    if (!team?.clientId || this.bannedClientIds.length === 0) return false;
    return this.bannedClientIds.includes(team.clientId);
  }

  /** True when the currently selected scheduling team belongs to a banned client. */
  get selectedTeamBanned(): boolean {
    return this.selectedTeam ? this.isTeamBanned(this.selectedTeam) : false;
  }

  get modalHeader(): string {
    return this.isEditMode ? "Edit Driver Assignment" : "Assign Driver to Scheduling Team";
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  onTeamChange(): void {
    this.selectedBoardId = null;
    this.selectedBillToId = null;
  }

  onEquipmentChange(): void {
    this.equipmentTouched = true;
  }

  close(): void {
    this.visibleChange.emit(false);
  }

  async confirm(): Promise<void> {
    if (!this.selectedTeamId || !this.driverId || this.isSubmitting) return;

    // A driver cannot be placed on a scheduling group/board whose client they
    // are banned from.
    if (this.selectedTeamBanned) {
      this.toastr.error(
        `This driver is banned from client "${this.selectedTeam?.clientName || "this client"}" and cannot be assigned to "${this.selectedTeam?.name || "this scheduling group"}".`
      );
      return;
    }

    this.isSubmitting = true;

    // Capture state before the async call completes (close() may clear fields)
    const isUpdate = this.effectiveExistingTeamId === this.selectedTeamId;
    const team = this.selectedTeam;
    const boardName =
      this.availableBoards.find((b: any) => b.id === this.selectedBoardId)?.name ?? null;
    const billToClient = this.availableBillToClients.find(
      (c: any) => c.id === this.selectedBillToId
    );
    const capturedBoardId = this.selectedBoardId;
    const capturedBillToId = this.selectedBillToId;
    const capturedNotify = this.notifyOnAssign;
    const capturedTractorId = this.selectedTractorId;
    const capturedTrailerId = this.selectedTrailerId;

    // Whether tractor/trailer will be synced to the exact selected values.
    // ReplaceEquipment tells the server that an empty list means "clear", so
    // removals persist (an empty tractorIds/trailerIds array removes that
    // assignment).
    const equipmentChanged =
      this.equipmentTouched ||
      capturedTractorId !== this.originalTractorId ||
      capturedTrailerId !== this.originalTrailerId;

    // Assigning a tractor/trailer that another driver currently holds will
    // unassign that driver (the "SHIPPER" trailer is the exception: it can be
    // assigned to multiple drivers, so it never displaces anyone). Preview the
    // assignment before committing so the user can confirm or cancel.
    if (equipmentChanged) {
      let canProceed = true;
      try {
        const preview: any = await this.http
          .post("api/dispatch/assign", {
            driverIds: [this.driverId],
            tractorIds: capturedTractorId ? [capturedTractorId] : [],
            trailerIds: capturedTrailerId ? [capturedTrailerId] : [],
            replaceEquipment: true,
            dryRun: true,
          })
          .toPromise();
        const displaced: any[] = preview?.displacedDrivers || [];
        if (displaced.length > 0) {
          const names = displaced
            .map((d: any) => d.name || d.Name || "")
            .filter(Boolean)
            .join(", ");
          canProceed = window.confirm(
            `Assigning this equipment will unassign it from the driver(s) currently holding it: ${names}.\n\nContinue?`
          );
        }
      } catch {
        // Preview could not be computed; allow the assignment attempt to proceed.
        canProceed = true;
      }

      if (!canProceed) {
        this.isSubmitting = false;
        return;
      }
    }

    const assignmentObs = isUpdate
      ? this.http.patch("api/board/updateDriverAssignment", {
          schedulingTeamId: this.selectedTeamId,
          driverId: this.driverId,
          boardId: capturedBoardId,
          billToClientId: capturedBillToId,
        })
      : this.http.post("api/board/addDrivers", {
          schedulingTeamId: this.selectedTeamId,
          driverAssignments: [
            {
              driverId: this.driverId,
              boardName,
              billToClientId: capturedBillToId,
            },
          ],
        });

    assignmentObs.subscribe({
      next: () => {
        const equipmentUpdate$ = equipmentChanged
          ? this.http.post("api/dispatch/assign", {
              driverIds: [this.driverId],
              tractorIds: capturedTractorId ? [capturedTractorId] : [],
              trailerIds: capturedTrailerId ? [capturedTrailerId] : [],
              replaceEquipment: true,
              dryRun: false,
            })
          : null;

        const finish = () => {
          this.isSubmitting = false;
          this.toastr.success(
            isUpdate ? "Driver assignment updated" : "Driver assigned successfully"
          );

          const result: AssignDriverResult = {
            team,
            boardId: capturedBoardId,
            boardName,
            billToClientId: capturedBillToId,
            billToClientName: billToClient?.companyName || null,
            isUpdate,
          };

          this.close();

          if (isUpdate) {
            this.updateSuccess.emit(result);
          } else {
            this.assignSuccess.emit(result);
            this.maybeShowTrainingNotes(team);
          }

          if (capturedNotify) {
            this.prepareEmailCompose(team, boardName);
          }
        };

        if (equipmentUpdate$) {
          equipmentUpdate$.subscribe({
            next: finish,
            error: () => {
              // Assignment succeeded; only equipment update failed - still consider success but warn
              this.toastr.warning("Assignment updated, but failed to update equipment");
              finish();
            },
          });
        } else {
          finish();
        }
      },
      error: (err: any) => {
        this.isSubmitting = false;
        this.toastr.error(err || "Error saving assignment");
      },
    });
  }

  unassign(): void {
    if (!this.driverId || !this.effectiveExistingTeamId || this.isSubmitting) return;
    this.isSubmitting = true;
    const driverId = this.driverId;
    const teamId = this.effectiveExistingTeamId;
    this.http
      .post("api/board/removeDrivers", {
        schedulingTeamId: teamId,
        driverIds: [driverId],
      })
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.toastr.success("Driver removed from scheduling team");
          this.close();
          this.unassignSuccess.emit();
          this.unassignWithNote.emit({ driverId, teamId });
        },
        error: (err: any) => {
          this.isSubmitting = false;
          this.toastr.error(err || "Failed to remove driver");
        },
      });
  }

  // ── Training notes ─────────────────────────────────────────────────────────

  private maybeShowTrainingNotes(team: any): void {
    if (!team?.clientCustomerTraining) return;
    this.trainingNotesContent = team.clientCustomerTraining;
    this.trainingNotesClientName = team.clientName || null;
    this.showTrainingNotes = true;
  }

  dismissTrainingNotes(): void {
    this.showTrainingNotes = false;
    this.trainingNotesContent = null;
    this.trainingNotesClientName = null;
  }

  // ── Email compose ──────────────────────────────────────────────────────────

  private prepareEmailCompose(team: any, _boardName: string | null): void {
    const contacts = (team?.contacts || []).filter((c: any) => !c.doNotContact && c.email);
    const toAddresses: string[] = contacts.map((c: any) => c.email);
    this.emailTo = toAddresses.join("; ");
    this.emailCc = this.driverNotesCcEmail;
    this.emailBcc = (this.driverDetail as any)?.email || "";

    const driverName = this.driverDetail ? getDriverDisplayName(this.driverDetail) : "Driver";
    this.emailSubject = `Driver Assignment \u2013 ${driverName}`;
    this.emailBody = `${driverName} has been assigned to ${
      capitalizeWords(team?.name) || "a scheduling group"
    }.`;
    this.showEmailCompose = true;
  }

  sendEmail(): void {
    if (this.isSendingEmail) return;
    this.isSendingEmail = true;
    this.http
      .post("api/board/notifyDriverAssigned", {
        to: this.emailTo || null,
        cc: this.emailCc || null,
        bcc: this.emailBcc || null,
        subject: this.emailSubject,
        body: this.emailBody,
      })
      .subscribe({
        next: () => {
          this.isSendingEmail = false;
          this.toastr.success("Notification sent");
          this.closeEmailCompose();
        },
        error: () => {
          this.isSendingEmail = false;
          this.toastr.error("Failed to send notification");
        },
      });
  }

  closeEmailCompose(): void {
    this.showEmailCompose = false;
  }
}
